import {Space} from "antd";
import {OfferingFormAccordionItem} from "../../../components/offering-form/accordion/accordion-item";
import {SubscriptionCoverImagesFormFields} from "../../../components/offering-form/subscriptions/cover-images";
import type {SubscriptionCoverImagesFormFieldsProps} from "../../../components/offering-form/subscriptions/cover-images/types";
import {
  useSubscriptionCoverImagesDelete,
  useSubscriptionUploadCoverImages,
} from "../../../react-query-hooks/store/subscriptions/upload-content";
import {useGetSubscriptionApi} from "../../../react-query-hooks/store/subscriptions/get-subscription";
import type {CoverImagesItemResponse} from "../../../react-query-hooks/products/types";
import type {GetSubscriptionApiResponse} from "../../../react-query-hooks/store/subscriptions/get-subscription/types";

type SubscriptionProduct = GetSubscriptionApiResponse["productSubscription"];

/** Partial subscription from create response or full GET — only id + cover_images are read. */
export type SubscriptionCoverImagesFallback = {
  id: number;
  cover_images?:
    | SubscriptionProduct["cover_images"]
    | Array<{id: string; media_url: string; offering_id: number}>;
};

function mapToCoverImagesItemResponse(
  images: SubscriptionCoverImagesFallback["cover_images"]
): CoverImagesItemResponse[] {
  return (images ?? []).map((img) => {
    const row = img as Record<string, unknown>;
    const media_url = String(row.media_url ?? row.mediaUrl ?? row.url ?? "");
    return {
      id: String(row.id ?? row.mediaId ?? ""),
      media_url,
      offering_id: Number(row.offering_id ?? row.offeringId ?? 0),
      createdAt: typeof row.createdAt === "string" ? row.createdAt : "",
      updatedAt: typeof row.updatedAt === "string" ? row.updatedAt : "",
      media_type: "image" as const,
      offering_type: "product" as const,
      user_id: typeof row.user_id === "number" ? row.user_id : 0,
    };
  });
}

/**
 * Cover image upload + list for a subscription, backed by GET subscription + v2 upload mutation cache updates.
 */
export function SubscriptionCoverImagesBlock({
  subscriptionId,
  fallbackProductSubscription,
  accordionTitle = "Cover image",
}: {
  subscriptionId: number;
  /** Used until GET subscription resolves (e.g. create flow right after POST). */
  fallbackProductSubscription?: SubscriptionCoverImagesFallback | null;
  accordionTitle?: string;
}) {
  const getSubscriptionQuery = useGetSubscriptionApi(subscriptionId);

  const subscription =
    getSubscriptionQuery.data?.productSubscription ?? fallbackProductSubscription ?? null;

  const coverImages = mapToCoverImagesItemResponse(subscription?.cover_images);

  const subscriptionUploadCoverImagesMutation = useSubscriptionUploadCoverImages();
  const subscriptionCoverImagesDeleteMutation = useSubscriptionCoverImagesDelete();

  function handleCoverImagesItemDelete({
    mediaId,
    offeringId,
  }: {
    mediaId: string;
    offeringId: number;
  }) {
    subscriptionCoverImagesDeleteMutation.mutate(
      {mediaId, storeServiceId: offeringId},
      {
        onSuccess: () => {
          void getSubscriptionQuery.refetch();
        },
      }
    );
  }

  return (
    <Space size={16} direction="vertical" style={{display: "flex"}}>
      <OfferingFormAccordionItem key={accordionTitle} index={0} title={accordionTitle}>
        <SubscriptionCoverImagesFormFields
          key={`subscription-${subscriptionId}-covers`}
          accordionIndex={0}
          defaultCoverImages={coverImages}
          deleteCoverImage={handleCoverImagesItemDelete}
          isDeleteLoading={subscriptionCoverImagesDeleteMutation.isLoading}
          offeringId={subscriptionId}
          isUploadLoading={subscriptionUploadCoverImagesMutation.isLoading}
          uploadCoverImages={
            subscriptionUploadCoverImagesMutation as SubscriptionCoverImagesFormFieldsProps["uploadCoverImages"]
          }
        />
      </OfferingFormAccordionItem>
    </Space>
  );
}
